/* @flow */
import cx from 'classnames';
import React from 'react';
import ReactDOM from 'react-dom';
import Button from 'components/Button.jsx';
import Buttons from 'components/Buttons.jsx';
import BoxBody from 'components/BoxBody.jsx';
import Dialog from 'components/Dialog.jsx';
import LoadingIndicator from 'components/LoadingIndicator.jsx';
import createChainedFunction from 'utils/createChainedFunction';
import focusNode from 'utils/focusNode';

import './styles/ConfirmDialog.scss';

/**
 * A simple dialog  for displaying a message with the option to accept or cancel.
 *
 * Use with [ModalTrigger](/components/ModalTrigger)
 *
 * ```example
 *  <ModalTrigger
 *    modal={
 *      <ConfirmDialog>
 *        Hello World!
 *      </ConfirmDialog>
 *    }>
 *    <Button>Show dialog</Button>
 *  </ModalTrigger>
 * ```
 */

class ConfirmDialog extends React.Component {
  static propTypes = {
    ...Dialog.propTypes,
    actionButtonText: React.PropTypes.string,
    cancelButtonText: React.PropTypes.string,
    children: React.PropTypes.node,
    eventContext: React.PropTypes.string,
    onCancel: React.PropTypes.func,
    onConfirm: React.PropTypes.func,
    onRequestHide: React.PropTypes.func,
    preConfirmPromise: React.PropTypes.func,
    confirmDisabled: React.PropTypes.bool,
    hideCancel: React.PropTypes.bool,
    actionButtonColor: React.PropTypes.string,
    divided: React.PropTypes.bool,
    staticModal: React.PropTypes.bool,
  };

  static defaultProps = {
    actionButtonText: 'Yes',
    cancelButtonText: 'No',
    preConfirmPromise: () => Promise.resolve(),
    onConfirm: () => {},
    size: 'xsmall',
    hideCancel: false,
    divided: false,
    staticModal: true,
  };

  state = {
    loading: false,
  };

  handleCancelButtonClick = () => {
    this.props.onRequestHide();
    if (this.props.onCancel) {
      this.props.onCancel();
    }
  };

  handleConfirmButtonClick = () => {
    this.setState({ loading: true });

    if (this.props.staticModal) {
      this.props.preConfirmPromise()
        .then(this.props.onRequestHide)
        .then(this.props.onConfirm)
        .catch(err => console.error('Static modal pre confirm failure', err));
    } else {
      this.props.preConfirmPromise()
        .then(this.props.onConfirm)
        .catch(err => console.error('Pre confirm failure', err));
    }
  };

  renderCancelButton = () => {
    return !this.props.hideCancel &&
      <Button
        key="confirm-cancel"
        className="cancel"
        eventElementName="ConfirmDialog-cancelButton"
        eventContextName={this.props.eventContext}
        color="brand"
        level="text"
        onClick={this.handleCancelButtonClick}>
        {this.props.cancelButtonText}
      </Button>;
  };

  renderActionButton = () => {
    return (
      <Button
        data-test-selector="confirm_dialog_action_button"
        key="confirm-action"
        className="action"
        eventElementName="ConfirmDialog-actionButton"
        eventContextName={this.props.eventContext}
        disabled={this.props.confirmDisabled}
        onClick={this.handleConfirmButtonClick}
        color={this.props.actionButtonColor}
        ref={component => focusNode(ReactDOM.findDOMNode(component))}>
        {this.props.actionButtonText}
      </Button>
    );
  };

  renderBoxBody = () => {
    if (this.state.loading) {
      return (
        <BoxBody divided={this.props.divided} >
          <LoadingIndicator />
        </BoxBody>
      );
    } else {
      return (
        <BoxBody divided={this.props.divided}>
          <div className={cx({ 'ConfirmDialog-text': !!this.props.children })}>
            {this.props.children}
          </div>
          <Buttons axis="y">
            {this.renderActionButton()}
            {this.renderCancelButton()}
          </Buttons>
        </BoxBody>
      );
    }
  };

  render() {
    return (
      <Dialog
        {...this.props}
        className={cx('ConfirmDialog', this.props.className)}
        onRequestHide={createChainedFunction(this.props.onCancel, this.props.onRequestHide)}>
        {this.renderBoxBody()}
      </Dialog>
    );
  }
}

export default ConfirmDialog;



// WEBPACK FOOTER //
// ./src/components/ConfirmDialog.jsx